import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePortal } from '@/contexts/PortalContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CreditCard, Download, CheckCircle, Clock, AlertTriangle } from 'lucide-react';

export default function PortalBilling() {
  const { api, user } = usePortal();
  const [data, setData] = useState(null);
  const pid = user?.projects?.[0];

  useEffect(() => { if (pid) api(`/projects/${pid}/billing`).then(r => setData(r.data)).catch(() => {}); }, [api, pid]);

  if (!data) return <div className="h-48 bg-white rounded-xl animate-pulse" />;
  const statusIcon = { paid: <CheckCircle className="w-4 h-4 text-emerald-500" />, unpaid: <Clock className="w-4 h-4 text-amber-500" />, overdue: <AlertTriangle className="w-4 h-4 text-red-500" />, partial: <Clock className="w-4 h-4 text-orange-500" /> };
  const statusColor = { paid: 'bg-emerald-50 text-emerald-700', unpaid: 'bg-amber-50 text-amber-700', overdue: 'bg-red-50 text-red-700', partial: 'bg-orange-50 text-orange-700' };

  return (
    <div className="space-y-6" data-testid="portal-billing">
      <div>
        <h1 className="text-2xl font-extrabold text-[#1e3a50]">Billing & Payments</h1>
        <p className="text-sm font-medium text-[#6b8a9e]">Your payment summary and history</p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)]"><CardContent className="p-4 text-center">
          <p className="text-xl font-extrabold font-mono text-[#1e3a50]">${data.total_due.toLocaleString()}</p>
          <p className="text-[9px] font-bold text-[#6b8a9e] uppercase">Total Due</p>
        </CardContent></Card>
        <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)]"><CardContent className="p-4 text-center">
          <p className="text-xl font-extrabold font-mono text-emerald-700">${data.total_paid.toLocaleString()}</p>
          <p className="text-[9px] font-bold text-[#6b8a9e] uppercase">Total Paid</p>
        </CardContent></Card>
        <Card className={`border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)] ${data.balance > 0 ? 'bg-amber-50' : 'bg-emerald-50'}`}><CardContent className="p-4 text-center">
          <p className={`text-xl font-extrabold font-mono ${data.balance > 0 ? 'text-amber-800' : 'text-emerald-700'}`}>${data.balance.toLocaleString()}</p>
          <p className="text-[9px] font-bold text-[#6b8a9e] uppercase">Balance</p>
        </CardContent></Card>
      </div>

      {/* Invoices */}
      <div>
        <h2 className="text-sm font-bold text-[#1e3a50] mb-3">Invoices</h2>
        <div className="space-y-2">
          {data.invoices.map(inv => (
            <Card key={inv.id} className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
              <CardContent className="p-3 flex items-center gap-3">
                {statusIcon[inv.status] || statusIcon.unpaid}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-[#1e3a50]">{inv.title || inv.description || 'Invoice'}</p>
                  <p className="text-[10px] font-medium text-[#8faab8]">Due: {inv.due_date}</p>
                </div>
                <span className="text-base font-extrabold font-mono text-[#1e3a50]">${(inv.amount_due || 0).toLocaleString()}</span>
                <Badge className={`text-[9px] font-bold capitalize ${statusColor[inv.status] || statusColor.unpaid}`}>{inv.status}</Badge>
              </CardContent>
            </Card>
          ))}
          {data.invoices.length === 0 && <p className="text-center py-6 text-sm font-medium text-[#6b8a9e]">No invoices yet</p>}
        </div>
      </div>

      {/* Payments */}
      {data.payments.length > 0 && (
        <div>
          <h2 className="text-sm font-bold text-[#1e3a50] mb-3">Payment History</h2>
          <div className="space-y-2">
            {data.payments.map(pay => (
              <Card key={pay.id} className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
                <CardContent className="p-3 flex items-center gap-3">
                  <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  <div className="flex-1"><p className="text-sm font-bold text-[#1e3a50]">{pay.description || 'Payment'}</p><p className="text-[10px] text-[#8faab8]">{pay.date}</p></div>
                  <span className="text-sm font-bold font-mono text-emerald-700">${(pay.amount || 0).toLocaleString()}</span>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      <p className="text-[10px] text-[#8faab8] text-center">To make a payment, contact HDH Pools at (435) 555-0100 or reply in Support.</p>
    </div>
  );
}
